import { apiService } from './api';

/**
 * Templates service for handling template-related API calls
 */
export const templatesService = {
  /**
   * Get all templates for a project with filtering and pagination
   * @param {string} projectId - Project ID
   * @param {Object} filters - Filter and pagination options
   * @param {string} [filters.category] - Filter by category
   * @param {string} [filters.status] - Filter by status
   * @param {string} [filters.search] - Search in name and component text
   * @param {string} [filters.sortBy] - Sort field
   * @param {string} [filters.sortOrder] - Sort order (asc/desc)
   * @param {number} [filters.page] - Page number
   * @param {number} [filters.limit] - Items per page
   * @returns {Promise<Object>} Templates response with pagination
   */
  getTemplates: async (projectId, filters = {}) => {
    // Filter out empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    const params = new URLSearchParams({ projectId, ...cleanFilters });
    return await apiService.get(`/templates?${params}`);
  },

  /**
   * Get a specific template by ID
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Template response
   */
  getTemplate: async (projectId, templateId) => {
    const params = new URLSearchParams({ projectId });
    return await apiService.get(`/templates/${templateId}?${params}`);
  },

  /**
   * Create a new template
   * @param {string} projectId - Project ID
   * @param {Object} templateData - Template data
   * @param {string} templateData.name - Template name
   * @param {string} templateData.category - Template category
   * @param {string} templateData.parameterFormat - Parameter format
   * @param {string} [templateData.language] - Language code
   * @param {Array} templateData.components - Template components
   * @returns {Promise<Object>} Created template response
   */
  createTemplate: async (projectId, templateData) => {
    return await apiService.post(`/templates`, {
      projectId,
      ...templateData,
    });
  },

  /**
   * Update an existing template
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated template response
   */
  updateTemplate: async (projectId, templateId, templateData) => {
    return await apiService.put(`/templates/${templateId}`, {
      projectId,
      ...templateData,
    });
  },

  /**
   * Delete a template
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Delete response
   */
  deleteTemplate: async (projectId, templateId) => {
    const params = new URLSearchParams({ projectId });
    return await apiService.delete(`/templates/${templateId}?${params}`);
  },

  /**
   * Get template statistics for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Template statistics response
   */
  getTemplateStats: async projectId => {
    const params = new URLSearchParams({ projectId });
    return await apiService.get(`/templates/stats?${params}`);
  },

  /**
   * Check template edit restrictions
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Edit restrictions response
   */
  getEditRestrictions: async (projectId, templateId) => {
    const params = new URLSearchParams({ projectId });
    return await apiService.get(
      `/templates/${templateId}/edit-restrictions?${params}`
    );
  },

  /**
   * Validate template data
   * @param {string} projectId - Project ID
   * @param {Object} templateData - Template data to validate
   * @param {string} [templateId] - Template ID for update validation
   * @returns {Promise<Object>} Validation response
   */
  validateTemplate: async (projectId, templateData, templateId = null) => {
    const payload = { projectId, ...templateData };
    if (templateId) {
      payload.templateId = templateId;
    }
    return await apiService.post('/templates/validate', payload);
  },

  /**
   * Duplicate a template
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID to duplicate
   * @param {string} newName - Name for the duplicated template
   * @returns {Promise<Object>} Duplicated template response
   */
  duplicateTemplate: async (projectId, templateId, newName) => {
    return await apiService.post(`/templates/${templateId}/duplicate`, {
      projectId,
      newName,
    });
  },

  /**
   * Submit template for approval
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Submission response
   */
  submitForApproval: async (projectId, templateId) => {
    return await apiService.post(`/templates/${templateId}/submit`, {
      projectId,
    });
  },

  /**
   * Pause a template
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Pause response
   */
  pauseTemplate: async (projectId, templateId) => {
    return await apiService.put(`/templates/${templateId}/pause`, {
      projectId,
    });
  },

  /**
   * Resume a paused template
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Resume response
   */
  resumeTemplate: async (projectId, templateId) => {
    return await apiService.put(`/templates/${templateId}/resume`, {
      projectId,
    });
  },

  /**
   * Get template preview
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @param {Object} [exampleData] - Example data for preview
   * @returns {Promise<Object>} Template preview response
   */
  getTemplatePreview: async (projectId, templateId, exampleData = {}) => {
    return await apiService.post(`/templates/${templateId}/preview`, {
      projectId,
      exampleData,
    });
  },

  /**
   * Test template with sample data
   * @param {string} projectId - Project ID
   * @param {string} templateId - Template ID
   * @param {string} phoneNumber - Phone number to send test to
   * @param {Object} [exampleData] - Example data for test
   * @returns {Promise<Object>} Test response
   */
  testTemplate: async (
    projectId,
    templateId,
    phoneNumber,
    exampleData = {}
  ) => {
    return await apiService.post(`/templates/${templateId}/test`, {
      projectId,
      phoneNumber,
      exampleData,
    });
  },
};

export default templatesService;
